import {
  ProcedureExecutionSnapshotSchema,
  type ProcedureExecutionSnapshot,
} from '@erp/shared-types';
import { attachOwnedProcedures } from './procedureOwnership';
import { nestUnderWorkflowSteps } from './workflowPhaseSteps';
import { mergeStreamedReasoning } from './mergeStreamedReasoning';
import {
  actorOf,
  branchChoiceOf,
  collectProcedureCalls,
  readProcedureRow,
  thinkingOf,
} from './traceDetail';
import { z } from 'zod';
import type {
  AgentMessageResponse,
  ToolCallRunResponse,
  WorkflowPhaseStep,
} from '@erp/rpc';
import { OPERATOR_TOOL_TYPE, OPERATOR_AGENT_CARD } from '@erp/shared-types';
import { actionDetail } from 'src/components/common/agentActionFormatting';
import {
  SOP_REPLAY_STEP_TOOL_TYPE,
  procedureKey,
  readSopTag,
  sopVerifyState,
  stepKey,
} from 'src/components/common/sopReplayCore';
import type {
  Span,
  SpanBrief,
  SpanHumanTakeover,
  FlatRow,
  SpanType,
  SpanStatus,
  TokenSum,
  TraceFilters,
} from './types';

/**
 * The audit row a human intervention in an operator run is persisted as (AI-244) — written by
 * `operatorTakeoverTrace.ts` on the backend, `running` while the human decides and completed with
 * the outcome. One toolType for every outcome; `outputData.outcome` says which.
 */
export const HUMAN_TAKEOVER_TOOL_TYPE = 'agent.web_browser.human_takeover';

/**
 * The `outputData` bag of a human-takeover row, as `operatorTakeoverTrace.ts` writes it. Every
 * field is optional so a row still `running` (only `outcome: "paused"`) or one written by an
 * older backend still parses; an unparseable bag reads as a pause in progress.
 */
const HumanTakeoverOutputSchema = z.object({
  outcome: z
    .enum(['paused', 'instruct', 'takeover', 'cancelled', 'timed_out'])
    .default('paused'),
  instruction: z.string().nullish(),
  injectedText: z.string().nullish(),
  resumedBy: z.object({ name: z.string().nullish() }).nullish(),
  pausedAt: z.string().nullish(),
  resolvedAt: z.string().nullish(),
});

/** Read the human-takeover facts off a `HUMAN_TAKEOVER_TOOL_TYPE` row's output. */
function humanTakeoverOf(tc: ToolCallRunResponse): SpanHumanTakeover {
  const parsed = HumanTakeoverOutputSchema.safeParse(tc.outputData ?? {});
  const out = parsed.success ? parsed.data : { outcome: 'paused' as const };
  return {
    outcome: out.outcome,
    instruction: trimmed(out.instruction),
    byName: trimmed(out.resumedBy?.name),
    injectedText: trimmed(out.injectedText),
    pausedAt: trimmed(out.pausedAt) ?? trimmed(tc.inputData.pausedAt),
    resolvedAt: trimmed(out.resolvedAt),
  };
}

// Tools whose handler runs its own internal LLM loop. They render as named
// subagents (their per-iteration model calls / actions nest beneath) rather
// than as a generic tool row. Keyed by the tool-call `toolType`.
const SUBAGENT_TOOL_NAMES: Record<string, string> = {
  [OPERATOR_TOOL_TYPE]: OPERATOR_AGENT_CARD.name, // agent.web_browser.execute
  'agent.remote_desktop.execute': 'Remote Desktop Operator',
};

/** The tool-call id of a step's only operator call: the host of any replay row too old to record
 * its own `parentToolCallId`. Null when the step made no operator call, or several — the owner is
 * then ambiguous, and keying by the ordinal alone is the honest answer. */
function soleOperatorHost(calls: ToolCallRunResponse[]): string | null {
  const operators = calls.filter(
    (tc) => SUBAGENT_TOOL_NAMES[tc.toolType ?? ''],
  );
  return operators.length === 1 ? (operators[0].toolCallId ?? null) : null;
}

/**
 * The browser operator's final summary (its `done` thought) for a run — the
 * `message` on the last `agent.web_browser.execute` tool call's output. Null when
 * the operator hasn't finished (or the run used no web-browser tool).
 */
export function operatorFinalResponse(
  toolCalls: ToolCallRunResponse[] | undefined,
): string | null {
  const calls = (toolCalls ?? []).filter(
    (tc) => tc.toolType === 'agent.web_browser.execute',
  );
  const out = calls[calls.length - 1]?.outputData as
    Record<string, unknown> | null | undefined;
  return typeof out?.message === 'string' && out.message.trim()
    ? out.message
    : null;
}

export function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms >= 10000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function fmtTokens(n: number | null | undefined): string {
  if (n == null || n === 0) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function parseDate(d: string | null | undefined): Date | null {
  if (!d) return null;
  return new Date(d);
}

function simplifyToolName(toolType: string): string {
  const parts = toolType.split('.');
  return parts[parts.length - 1] ?? toolType;
}

export function formatToolDisplayName(name: string): string {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function deriveLlmStepName(
  stepToolCalls: ToolCallRunResponse[],
  stepNumber: number | null | undefined,
  idx: number,
  isLast: boolean,
  finishReason: string | null | undefined,
): string {
  if (isLast && finishReason === 'stop') return 'Stop';
  if (stepToolCalls.length > 0) {
    const distinct = [
      ...new Set(
        stepToolCalls.map((tc) =>
          formatToolDisplayName(simplifyToolName(tc.toolType ?? 'tool')),
        ),
      ),
    ];
    // A single tool call reads cleanest named after the tool itself.
    if (stepToolCalls.length === 1) return distinct[0] ?? 'Tool';
    // Multiple calls: summarize as a count plus the first few distinct tool
    // names, so a parallel turn isn't mislabeled after just its first child.
    const shown = distinct.slice(0, 3);
    const remaining = distinct.length - shown.length;
    const names =
      remaining > 0
        ? `${shown.join(', ')}, +${remaining} more`
        : shown.join(', ');
    return `${stepToolCalls.length} tool calls (${names})`;
  }
  return stepNumber != null ? `Step ${stepNumber + 1}` : `Action ${idx + 1}`;
}

/**
 * What this tool call actually did. The operator passes the action explicitly (`left_click`,
 * `key`); every other tool IS its action, so fall back to the tool name. Both the row's glyph and
 * its argument chip key off this, so they can never disagree about which action a row is.
 */
function toolAction(tc: ToolCallRunResponse, toolType: string): string {
  return typeof tc.inputData.action === 'string'
    ? tc.inputData.action
    : simplifyToolName(toolType);
}

/**
 * The operator's own words for this action ("Grid didn't load — retrying the click").
 *
 * It rides the durable audit row's `outputData` — the same field `buildToolCallMessages` reads to
 * caption a screenshot — so a nested operator trail can read as prose instead of a wall of clicks.
 * Null on tool calls whose handler runs no model.
 */
function toolReasoning(tc: ToolCallRunResponse): string | null {
  const reasoning = tc.outputData?.reasoning;
  return typeof reasoning === 'string' && reasoning.trim() ? reasoning : null;
}

function trimmed(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Where to read this action's arguments from.
 *
 * An operator action's args ride a nested `actionArgs` (projected by `makeOperatorStepPump` from
 * the canonical step, which never reaches `inputData` flat). Every other tool call carries its
 * arguments at the top level, so fall back to the bag itself.
 */
function actionArgsOf(
  inputData: Record<string, unknown>,
): Record<string, unknown> {
  const nested = inputData.actionArgs;
  return typeof nested === 'object' && nested !== null && !Array.isArray(nested)
    ? (nested as Record<string, unknown>)
    : inputData;
}

/**
 * What this tool call was working from, for the row's face.
 *
 * Two shapes, both previously reachable only by opening the detail panel and reading them out of
 * a pretty-printed JSON blob:
 *
 *   * the operator's `instruction` — the single line that explains what the dozen actions nested
 *     under a "Browser Operator" row are actually for;
 *   * the work item `get_next_task` pulled off the queue — its title, plus the description as
 *     supporting text, which is the whole reason the run exists.
 */
function toolBrief(
  tc: ToolCallRunResponse,
  toolType: string,
): SpanBrief | null {
  const instruction = trimmed(tc.inputData.instruction);
  if (instruction) return { kind: 'instruction', headline: instruction };

  if (toolType.includes('get_next_task')) {
    const output = tc.outputData;
    const task =
      typeof output?.task === 'object' && output.task !== null
        ? (output.task as Record<string, unknown>)
        : null;
    const headline = trimmed(task?.title);
    // A run that found nothing to do has no work item, and an empty label would be noise.
    if (headline) {
      return { kind: 'work_item', headline, body: trimmed(task?.description) };
    }
  }
  return null;
}

function buildToolSpan(
  tc: ToolCallRunResponse,
  runStart: number,
  fallbackStartMs: number,
  inferredDurMs?: number,
): Span {
  const tcStart = parseDate(tc.startedAt)?.getTime();
  const tcEnd = parseDate(tc.completedAt)?.getTime();
  const tcStartMs = tcStart ? Math.max(0, tcStart - runStart) : fallbackStartMs;
  const rawDurMs =
    typeof tc.duration === 'number' && tc.duration > 0
      ? tc.duration
      : tcStart && tcEnd && tcEnd > tcStart
        ? tcEnd - tcStart
        : null;
  const durMs = rawDurMs ?? inferredDurMs ?? null;

  const status: SpanStatus =
    tc.status === 'failed' || tc.status === 'timed_out'
      ? 'failed'
      : tc.status === 'running'
        ? 'running'
        : 'completed';

  // A tool whose handler runs its own LLM loop (browser / remote-desktop
  // operator) is a subagent: render it named ("Browser Operator") rather than as
  // the generic "execute" tool, regardless of which operator backend ran it.
  const toolType = tc.toolType ?? '';
  const subagentName = SUBAGENT_TOOL_NAMES[toolType];
  const action = toolAction(tc, toolType);

  const span: Span = {
    toolCallId: tc.toolCallId ?? undefined,
    id: tc.id,
    type: 'tool',
    // Title-cased, matching `liveActionSpan`. Without this a row reads `left_click` while it is
    // persisted and "Left Click" while it is live — the same action renaming itself mid-run,
    // since the two builders were formatting the tool name differently.
    name:
      subagentName ??
      formatToolDisplayName(simplifyToolName(toolType || 'unknown_tool')),
    isSubagent: subagentName != null,
    // The web-browser operator has a published A2A agent card; surface it so the
    // detail panel can show the operator's identity/skill.
    agentCard: toolType === OPERATOR_TOOL_TYPE ? OPERATOR_AGENT_CARD : null,
    status,
    startMs: tcStartMs,
    durMs,
    // Only the StepRun's own start → end; `inferredDurMs` is layout, not a measurement.
    timedDurMs: rawDurMs,
    action,
    detail: actionDetail(action, actionArgsOf(tc.inputData)),
    preview: tc.outputData ? JSON.stringify(tc.outputData).slice(0, 80) : null,
    reasoning: toolReasoning(tc),
    thinking: thinkingOf(tc.outputData),
    actor: actorOf(tc.inputData, action),
    brief: toolBrief(tc, toolType),
    toolInput: tc.inputData,
    toolOutput: tc.outputData ?? null,
    screenshotSignedUrl: tc.screenshotSignedUrl,
    stopReason: tc.stopReason,
    childAgentRunId: tc.childAgentRunId,
    childWorkflowRunId: tc.childWorkflowRunId,
  };
  return toolType === HUMAN_TAKEOVER_TOOL_TYPE
    ? asHumanTakeoverSpan(span, tc)
    : span;
}

/**
 * Re-shape a tool span built from a `HUMAN_TAKEOVER_TOOL_TYPE` row into the person's row: the
 * facts ride `human`, and the generic text fields are cleared because `SpanTable` renders its own
 * chip and line for them — a person's words must not be styled as the agent's reasoning.
 */
function asHumanTakeoverSpan(span: Span, tc: ToolCallRunResponse): Span {
  return {
    ...span,
    human: humanTakeoverOf(tc),
    detail: null,
    reasoning: null,
    brief: null,
    // The audit bag is fully projected onto `human`; leaving it here would make the detail panel
    // print the same facts again as raw JSON (user ids included) under "Input" / "Output".
    preview: null,
    toolInput: undefined,
    toolOutput: null,
  };
}

// Task-loop phases are persisted as loop-owned tool-call StepRuns with no
// agentMessageId (the loop itself runs no LLM step). Map their toolType to a
// readable label; these surface as top-level spans in the loop's trace.
const PHASE_TOOL_LABELS: Record<string, string> = {
  agent_setup: 'Setup',
  agent_teardown: 'Teardown',
  agent_task: 'Task',
  workflow_setup: 'Setup Workflow',
  workflow_teardown: 'Teardown Workflow',
};

function phaseSpanLabel(tc: ToolCallRunResponse): string | null {
  const toolType = tc.toolType ?? '';
  // Workflow phases: prefer the launched workflow's display name (recorded on
  // the phase StepRun's inputData), falling back to the generic label.
  if (toolType === 'workflow_setup' || toolType === 'workflow_teardown') {
    const workflowName = (tc.inputData as Record<string, unknown> | undefined)
      ?.workflowName;
    if (typeof workflowName === 'string' && workflowName.length > 0) {
      return workflowName;
    }
    return PHASE_TOOL_LABELS[toolType];
  }
  // Task phases: prefer the task's title (recorded on the phase StepRun's
  // inputData), falling back to the generic "Task" label for older runs.
  if (toolType === 'agent_task') {
    const taskTitle = (tc.inputData as Record<string, unknown> | undefined)
      ?.taskTitle;
    if (typeof taskTitle === 'string' && taskTitle.length > 0) {
      return `Task: ${taskTitle}`;
    }
    return PHASE_TOOL_LABELS[toolType];
  }
  if (PHASE_TOOL_LABELS[toolType]) return PHASE_TOOL_LABELS[toolType];
  // A loop-owned tool call that spawned a child run/workflow but uses an
  // unrecognized toolType: still surface it, with a best-effort name.
  if (tc.childAgentRunId || tc.childWorkflowRunId) {
    return formatToolDisplayName(simplifyToolName(toolType || 'phase'));
  }
  return null;
}

// A parent span must temporally cover its children. Two sources break this: a
// subagent tool call (e.g. web_browser_execute) records no span of its own
// (durMs null), and an LLM step's message is stamped *after* its tool ran — so a
// parent's latency reads shorter than the steps nested under it, and the column
// doesn't "sum up". Walk bottom-up and stretch each parent to span its children.
export function coverChildren(span: Span): void {
  if (!span.children || span.children.length === 0) return;
  for (const child of span.children) coverChildren(child);
  const start = Math.min(span.startMs, ...span.children.map((c) => c.startMs));
  const end = Math.max(
    span.startMs + (span.durMs ?? 0),
    ...span.children.map((c) => c.startMs + (c.durMs ?? 0)),
  );
  span.startMs = start;
  span.durMs = end - start;
}

/**
 * Time order for a span tree, after `coverChildren` has rewritten start times.
 *
 * Covering a parent pulls its start back to its earliest child, which can leave it sitting after a
 * sibling that now starts later. Sorting only the roots — what `buildSpans` used to do — leaves
 * that inversion one level down, so a live activity log reads out of order. Replayed procedure
 * steps stay in their own index order: that numbering is the procedure, and start time would
 * reshuffle it.
 */
export function sortSpanTreeByStart(spans: Span[]): Span[] {
  const sorted = [...spans].sort((a, b) => a.startMs - b.startMs);
  for (const span of sorted) {
    const children = span.children;
    if (!children?.length) continue;
    if (children.some((child) => child.sopStepIndex != null)) {
      for (const child of children) {
        if (child.children?.length) {
          child.children = sortSpanTreeByStart(child.children);
        }
      }
      continue;
    }
    span.children = sortSpanTreeByStart(children);
  }
  return sorted;
}

// ---------------------------------------------------------------------------
// SOP replay grouping: procedure → step → the operator actions that step made.
//
// When an agent replays a saved procedure (its own `run_procedure` tool, or a
// pinned procedure dispatched as a run_sop task) the backend tags every row the
// replay produced with `sopStepId` / `sopStepIndex` / `sopProcedureName` /
// `sopProcedureOrdinal`, and writes ONE `agent.sop_replay.step` row per step
// carrying that step's name, mode and Verify grade. Here that flat run of
// actions becomes two synthetic span levels, so the trace reads as
//   web_browser → Log into portal → step 2 → type / heal / type
// instead of a wall of clicks. The boundary rows are CONSUMED into the step
// spans — left alone they'd each show up as a junk sibling row.
// ---------------------------------------------------------------------------

type SopSpanTag = {
  /** Row id of the step the span belongs to (`sopStepSpanId`) — also the key steps fold by. */
  stepSpanId: string;
  stepIndex: number;
  /** Row id of its procedure (`sopProcedureSpanId`) — also the key procedures fold by. */
  procedureSpanId: string;
  procedureName: string;
  snapshot?: ProcedureExecutionSnapshot;
  parentToolCallId?: string;
};

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Where a span's replay tag lives: on its own tool input, or — for a token-bearing operator model
 * call, whose tag is on the action beneath it — on its first child. */
function sopTagSource(span: Span): Record<string, unknown> | undefined {
  return span.toolInput ?? span.children?.[0]?.toolInput;
}

/** The operator call that ran a replay row: the one it recorded, else the call it was found under
 * (`host`), which is all a row written before `parentToolCallId` existed has to go on. */
function replayOwnerOf(
  input: Record<string, unknown> | undefined,
  host: string | null,
): string | null {
  return str(input?.parentToolCallId) || host;
}

/** The row id of the procedure a replay row belongs to, under the call that ran it. */
function ownedProcedureSpanId(
  span: Span,
  ordinal: number,
  host: string | null,
): string {
  return sopProcedureSpanId(replayOwnerOf(span.toolInput, host), ordinal);
}

/** The replay tag on a span, or null when the span has nothing to do with a replay — which is every
 * span of an ordinary agentic run. Parsing is shared (`sopReplayCore.readSopTag`); this adds only
 * the span-shaped lookup and the row ids the fold groups by. */
function readSopSpanTag(span: Span, host: string | null): SopSpanTag | null {
  const source = sopTagSource(span);
  const tag = readSopTag(source);
  if (!tag) return null;
  const owner = replayOwnerOf(source, host);
  const ordinal = tag.procedureOrdinal;
  return {
    stepSpanId: sopStepSpanId(owner, ordinal, {
      stepId: tag.stepId,
      // readSopTag never returns a tag with neither, so a null index here has a stepId to key on.
      index: tag.stepIndex ?? 0,
    }),
    stepIndex: tag.stepIndex ?? 0,
    procedureSpanId: sopProcedureSpanId(owner, ordinal),
    procedureName: tag.procedureName ?? '',
    snapshot: ProcedureExecutionSnapshotSchema.safeParse(
      source?.procedureSnapshot,
    ).data,
    parentToolCallId: str(source?.parentToolCallId) ?? undefined,
  };
}

/**
 * The row ids for a replayed procedure and its steps. Two constraints shape them.
 *
 * PARITY. The LIVE stream builds the same rows before they are persisted, and the two must land on
 * identical ids or every step would appear twice — once from the stream, once from the timeline.
 * One definition, every caller, no chance of drift. It is also why the procedure is identified by
 * its ordinal and never its name: on the `run_sop` path the live side has no procedure name and
 * the persisted side carries Node's fallback title, so a name in the id put the two halves of one
 * replay under different rows and the dedup silently stopped working. See
 * `sopReplayCore.procedureKey`.
 *
 * UNIQUENESS. The ordinal only counts replays within ONE operator call, and every task of a task
 * loop is its own operator call opening with ordinal 1. Keyed by the ordinal alone, their
 * procedures shared a row id in one tree — and `SpanTable` keys rows and expansion by id, so a
 * finished task's procedure stayed rendered outside its collapsed task and its chevron toggled the
 * other one. Hence `owner`: the LLM tool-call id of the operator call that ran the replay, which
 * both sides know. Every persisted replay row records it as `parentToolCallId`; the live side reads
 * it off the operator call in flight (`resolveLiveSopReplay`). Null when there is no owner to be
 * had (a row recorded before ownership was, with no host call to stand in), which keys by the
 * ordinal alone — on both sides alike.
 */
export function sopProcedureSpanId(
  owner: string | null,
  ordinal: number,
): string {
  return `sop-procedure-${ownedProcedureKey(owner, ordinal)}`;
}

export function sopStepSpanId(
  owner: string | null,
  ordinal: number,
  step: { index: number; stepId: string | null },
): string {
  return `sop-step-${ownedProcedureKey(owner, ordinal)}|${stepKey(step.stepId, step.index)}`;
}

function ownedProcedureKey(owner: string | null, ordinal: number): string {
  const key = procedureKey(ordinal);
  return owner ? `${owner}|${key}` : key;
}

/**
 * Ids that occur more than once anywhere in the tree, collapsed rows included. `SpanTable` keys
 * rows and expansion by id, so a repeat renders a stale row and toggles the wrong subtree.
 */
export function duplicateSpanIds(spans: Span[]): string[] {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  const visit = (list: Span[]) => {
    for (const span of list) {
      if (seen.has(span.id)) repeated.add(span.id);
      seen.add(span.id);
      if (span.children) visit(span.children);
    }
  };
  visit(spans);
  return [...repeated];
}

/** Is this span one of the once-per-step boundary rows (the source of a step's header data)? */
function isSopBoundarySpan(span: Span): boolean {
  return (
    (span.toolInput?.sopStepName !== undefined &&
      span.toolInput?.sopStepMode !== undefined) ||
    span.name === simplifyToolName(SOP_REPLAY_STEP_TOOL_TYPE)
  );
}

function sopStepStatus(mode: string | null): SpanStatus {
  return mode === 'failed' ? 'failed' : 'completed';
}

/** Index the once-per-step boundary rows by procedure+step. Collected in a first pass because a
 * step's name/mode/verify arrive AFTER the actions they describe. */
function collectSopHeaders(
  spans: Span[],
  host: string | null,
): Map<string, Span> {
  const headers = new Map<string, Span>();
  for (const span of spans) {
    if (!isSopBoundarySpan(span)) continue;
    const tag = readSopSpanTag(span, host);
    if (tag) headers.set(tag.stepSpanId, span);
  }
  return headers;
}

/** The synthetic procedure row. A procedure runs its own steps and (when one drifts) its own healer
 * model calls, so it reads as a subagent — which is also what makes `aggregateTokens` surface a
 * heal's cost here rather than losing it. */
function makeProcedureSpan(tag: SopSpanTag): Span {
  return {
    toolInput: {
      procedureSnapshot: tag.snapshot,
      parentToolCallId: tag.parentToolCallId,
    },
    id: tag.procedureSpanId,
    type: 'tool',
    name: tag.procedureName || 'Procedure',
    isSubagent: true,
    isSopProcedure: true,
    actor: 'procedure',
    status: 'completed',
    startMs: 0,
    durMs: null,
    children: [],
  };
}

/** The once-per-replay procedure rows (`agent.sop_replay.procedure`), by procedure row id: the plan
 * snapshot as it was handed to the replay. Preferred over a snapshot carried on a step boundary,
 * which only older runs have. */
function collectProcedureRows(
  spans: Span[],
  host: string | null,
): Map<string, { snapshot: ProcedureExecutionSnapshot; row: Span }> {
  const rows = new Map<
    string,
    { snapshot: ProcedureExecutionSnapshot; row: Span }
  >();
  for (const span of spans) {
    const procedure = readProcedureRow(span);
    if (!procedure) continue;
    const id = ownedProcedureSpanId(span, procedure.ordinal, host);
    if (!rows.has(id))
      rows.set(id, { snapshot: procedure.snapshot, row: span });
  }
  return rows;
}

/** The tag a procedure row stands for when it is the first sign of its procedure. */
function procedureRowTag(
  row: Span,
  ordinal: number,
  host: string | null,
): SopSpanTag {
  const procedure = readProcedureRow(row);
  return {
    stepSpanId: '',
    stepIndex: 0,
    procedureSpanId: ownedProcedureSpanId(row, ordinal, host),
    procedureName:
      str(row.toolInput?.sopProcedureName) ?? procedure?.snapshot.title ?? '',
    snapshot: procedure?.snapshot,
    parentToolCallId: str(row.toolInput?.parentToolCallId) ?? undefined,
  };
}

/** The synthetic step row, filled from its boundary header when one has arrived. No header yet means
 * the step is still mid-replay. */
function makeStepSpan(
  id: string,
  tag: SopSpanTag,
  header: Span | undefined,
  snapshot: ProcedureExecutionSnapshot | undefined,
): Span {
  const input = header?.toolInput ?? {};
  const mode = str(input.sopStepMode);
  const planned = snapshot?.steps.find((step) => step.index === tag.stepIndex);
  return {
    id,
    type: 'tool',
    name: str(input.sopStepName) || `Step ${tag.stepIndex}`,
    status: header ? sopStepStatus(mode) : 'running',
    startMs: header?.startMs ?? 0,
    durMs: null,
    // The boundary row spans the step's start announcement → its close (older rows: close only).
    timedDurMs: header?.timedDurMs ?? null,
    sopStepMode: mode,
    sopStepNoop: input.sopStepNoop === true,
    sopStepIndex: tag.stepIndex,
    sopVerify: readSopVerify(input.sopStepVerify),
    sopStepDescription:
      str(input.sopStepDescription) ?? planned?.description ?? null,
    sopBranch: branchChoiceOf(input),
    stopReason: str(header?.toolOutput?.error),
    children: [],
  };
}

/** Prefer boundary evidence without discarding provenance already present on an operator action. */
function procedureTagWithHeader(
  tag: SopSpanTag,
  header: Span | undefined,
): SopSpanTag {
  const recorded = header ? readSopSpanTag(header, null) : null;
  return {
    ...tag,
    snapshot: recorded?.snapshot ?? tag.snapshot,
    parentToolCallId: tag.parentToolCallId ?? recorded?.parentToolCallId,
  };
}

/**
 * Fold a subagent's flat child spans into procedure → step → action.
 *
 * Untagged spans keep their position and order, so a run that never replayed a procedure comes out
 * of here byte-identical to what went in.
 *
 * `host` is the tool-call id of the operator call the spans were found under, when the caller knows
 * it: the owner of any row too old to record its own `parentToolCallId`. See `sopProcedureSpanId`
 * for why rows are keyed by their owner at all.
 */
export function groupSopReplaySpans(
  spans: Span[],
  host: string | null = null,
): Span[] {
  const headers = collectSopHeaders(spans, host);
  const procedureRows = collectProcedureRows(spans, host);
  if (
    headers.size === 0 &&
    procedureRows.size === 0 &&
    !spans.some((s) => readSopSpanTag(s, host))
  )
    return spans;

  const out: Span[] = [];
  const procedures = new Map<string, Span>();
  const steps = new Map<string, Span>();
  const calls = collectProcedureCalls(spans, (span, ordinal) =>
    ownedProcedureSpanId(span, ordinal, host),
  );

  /** The procedure row for a tag, creating it on first sight — which fixes its place in the
   * timeline. Its plan comes from the procedure row when one was persisted. */
  function procedureSpanFor(tag: SopSpanTag, header: Span | undefined): Span {
    const existing = procedures.get(tag.procedureSpanId);
    if (existing) return existing;
    const recorded = procedureRows.get(tag.procedureSpanId);
    const withHeader = procedureTagWithHeader(tag, header);
    const procedure = makeProcedureSpan({
      ...withHeader,
      snapshot: recorded?.snapshot ?? withHeader.snapshot,
      parentToolCallId:
        withHeader.parentToolCallId ??
        (str(recorded?.row.toolInput?.parentToolCallId) || undefined),
    });
    procedure.procedureCall = calls.get(tag.procedureSpanId) ?? null;
    procedures.set(tag.procedureSpanId, procedure);
    out.push(procedure);
    return procedure;
  }

  /** The step row for a tag, creating it (and its procedure row) on first sight. */
  function stepSpanFor(tag: SopSpanTag): Span {
    const key = tag.stepSpanId;
    const existingStep = steps.get(key);
    if (existingStep) return existingStep;

    const procedure = procedureSpanFor(tag, headers.get(key));
    const snapshot = ProcedureExecutionSnapshotSchema.safeParse(
      procedure.toolInput?.procedureSnapshot,
    ).data;
    const step = makeStepSpan(key, tag, headers.get(key), snapshot);
    steps.set(key, step);
    procedure.children?.push(step);
    return step;
  }

  for (const span of spans) {
    const procedureRow = readProcedureRow(span);
    if (procedureRow) {
      // Consumed into the procedure's own row; it opens that row if no step has yet.
      procedureSpanFor(
        procedureRowTag(span, procedureRow.ordinal, host),
        undefined,
      );
      continue;
    }
    const tag = readSopSpanTag(span, host);
    if (!tag) {
      out.push(span);
    } else if (isSopBoundarySpan(span)) {
      // Its data is already on the step row; keeping the row too would show it twice.
      stepSpanFor(tag);
    } else {
      stepSpanFor(tag).children?.push(span);
    }
  }

  // A step that produced no spans of its own (a branch node, or one that failed before acting)
  // still gets its row: it was visible while the run was live.
  for (const [key, header] of headers) {
    if (steps.has(key)) continue;
    const tag = readSopSpanTag(header, host);
    if (tag) stepSpanFor(tag);
  }

  // Runs stored before reasoning was coalesced hold a row per streamed prefix of each thought.
  for (const step of steps.values()) {
    step.children = mergeStreamedReasoning(step.children ?? [], isReasoningRow);
  }

  // A step whose boundary row hasn't landed — the one mid-replay — has no start of its own, and
  // neither does a procedure row. Left at 0, `coverChildren` would stretch every ancestor back to
  // the run's start, which drags the enclosing task phase ahead of the setup phases that really
  // came first (and, in the Actions view, open-ended over their actions).
  for (const [key, step] of steps) {
    if (!headers.has(key)) step.startMs = earliestStart(step.children) ?? 0;
  }
  for (const [key, procedure] of procedures) {
    const row = procedureRows.get(key)?.row;
    procedure.startMs =
      earliestStart([...(procedure.children ?? []), ...(row ? [row] : [])]) ??
      0;
  }

  for (const procedure of procedures.values()) {
    procedure.children?.sort(
      (a, b) => (a.sopStepIndex ?? 0) - (b.sopStepIndex ?? 0),
    );
    // A procedure whose steps all ran clean is 'completed'; any failed step fails the group.
    if (procedure.children?.some((c) => c.status === 'failed')) {
      procedure.status = 'failed';
    }
    procedure.timedDurMs = timedExtent(procedure.children ?? []);
  }
  return out;
}

/** The earliest start among spans, or null when there are none. */
function earliestStart(spans: readonly Span[] | undefined): number | null {
  if (!spans?.length) return null;
  return Math.min(...spans.map((span) => span.startMs));
}

/** A replay's bare reasoning row (`agent.sop_replay.action.reasoning`): words, no action, no frame. */
function isReasoningRow(span: Span): boolean {
  return span.action === 'reasoning' && !span.screenshotSignedUrl;
}

/** How long a run of steps took end to end, or null unless EVERY step was timed — one untimed step
 * would make the total a guess. */
function timedExtent(steps: Span[]): number | null {
  if (steps.length === 0) return null;
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const step of steps) {
    if (step.timedDurMs == null) return null;
    start = Math.min(start, step.startMs);
    end = Math.max(end, step.startMs + step.timedDurMs);
  }
  return end - start;
}

/** The step's Verify grade off a persisted tag bag, in the shape a Span carries. The "is there a
 * verdict at all" rule is shared — see `sopReplayCore.sopVerifyState`. */
function readSopVerify(raw: unknown): Span['sopVerify'] {
  if (typeof raw !== 'object' || raw === null) return null;
  const grade = raw as Record<string, unknown>;
  const kind = grade.kind;
  if (kind !== 'url_contains' && kind !== 'text_visible') return null;
  const state = sopVerifyState({
    kind,
    ran: grade.ran === true,
    passed: grade.passed === true,
  });
  return state === null ? null : { kind, passed: state === 'held' };
}

// Infer durations for audit sub-steps where startedAt === completedAt (duration = 0).
// Each step ran until the next step started; the last step ran until anchorMs.
function inferAuditDurations(spans: Span[], anchorMs: number): void {
  for (let i = 0; i < spans.length; i++) {
    if (spans[i].durMs != null && (spans[i].durMs ?? 0) > 0) continue;
    const nextStartMs = i + 1 < spans.length ? spans[i + 1].startMs : anchorMs;
    spans[i].durMs = Math.max(0, nextStartMs - spans[i].startMs);
  }
}

function auditMsgHasTokens(m: AgentMessageResponse): boolean {
  return (
    (m.inputTokens ?? null) !== null ||
    (m.outputTokens ?? null) !== null ||
    (m.cacheReadTokens ?? null) !== null ||
    (m.cacheWriteTokens ?? null) !== null
  );
}

// Audit sub-steps of a step. A token-bearing audit message is a subagent's own
// model call (e.g. the A2A operator's per-action LLM call): render it as a nested
// LLM span carrying that call's tokens, with its browser action as a tool child.
// Token-less audit messages keep the legacy behavior — their tool calls flatten
// into plain tool spans.
//
// One operator action is persisted TWICE — an iteration audit record AND the tool call it made
// (`persistOperatorSteps`) — so without the fold below the same click is two rows at two depths,
// and which shape you get depends on whether that particular record happened to carry token
// usage. Same action, two different tree shapes, for a reason no reader can see.
function buildAuditSubSpans(
  auditMsgs: AgentMessageResponse[],
  toolsByMessage: Map<string, ToolCallRunResponse[]>,
  runStart: number,
  fallbackStartMs: number,
  runModel: string | null,
): Span[] {
  // Cyclomatic 10 in this callback is the audit-row fold plus its token/model fallbacks — the
  // rule that makes one operator action one row instead of two. Under the ceiling of 20; the
  // overage is the estimated-coverage term of CRAP.
  // fallow-ignore-next-line complexity
  return auditMsgs.flatMap((am): Span[] => {
    const toolSpans = (toolsByMessage.get(am.id) ?? []).map((tc) =>
      buildToolSpan(tc, runStart, fallbackStartMs),
    );
    // Render as a nested model call when the iteration is a subagent's own LLM
    // call — it either carries token usage (A2A operator) or records the model
    // that ran it (remote-desktop / browser operator). Otherwise it's a plain
    // tool action and flattens.
    if (!auditMsgHasTokens(am) && !am.model) return toolSpans;
    const startMs = Math.max(0, new Date(am.createdAt).getTime() - runStart);
    const llmTokens = {
      inputTokens: am.inputTokens,
      outputTokens: am.outputTokens,
      reasoningTokens: am.reasoningTokens,
      cacheReadTokens: am.cacheReadTokens,
      cacheWriteTokens: am.cacheWriteTokens,
    };

    // The model call and the one action it produced are the same event. Fold them into a single
    // row — keeping the ACTION's name and glyph, since "Left Click" is what happened and a brain
    // over `left_click — I'm on the homepage…` reads as a separate thinking step that never was.
    // Same treatment `foldSingleActionStep` gives the outer step, for the same reason.
    const folded = foldSingleActionStep({
      children: toolSpans,
      msg: am,
      name: toolSpans[0]?.name ?? auditSpanName(am),
      llmTokens,
      fallbackModel: runModel,
      llmDurMs: null,
      eligible: toolSpans.length === 1,
    });
    if (folded) return [{ ...folded, startMs }];

    return [
      {
        id: am.id,
        type: 'llm',
        name: auditSpanName(am),
        model: am.model ?? runModel,
        status: am.finishReason === 'error' ? 'failed' : 'completed',
        startMs,
        durMs: null,
        ...llmTokens,
        preview: am.text?.slice(0, 100) ?? null,
        reasoning: am.reasoning,
        text: am.text,
        children: toolSpans,
      },
    ];
  });
}

/**
 * A name for an audit iteration that made no single action of its own.
 *
 * `persistOperatorSteps` writes the row's text as `<action> — <reasoning>`, which is a sentence,
 * not a label: as a row name it truncates mid-word and repeats prose the row already renders
 * beneath itself. Keep only the part before the dash.
 */
function auditSpanName(am: AgentMessageResponse): string {
  const text = am.text?.trim();
  if (!text) return 'Model call';
  const dash = text.indexOf(' — ');
  if (dash > 0) return formatToolDisplayName(text.slice(0, dash));
  return text.slice(0, 80);
}

// Roll token usage up the tree: every span gets a `rolled` total covering itself
// plus all descendants, so a turn/task/subagent row "sums up" the nested model
// calls beneath it rather than showing only its own call. The rolled total also
// surfaces the operator/child-run usage that would otherwise be invisible on a
// parent row. Also propagates the model of nested calls onto the subagent row.
// Idempotent: it only reads own token fields and never mutates them, so it can be
// re-run after child timelines are attached.
//
// Own tokens count whenever they are PRESENT, not when the span is typed `llm`. A tool span used
// to be guaranteed token-free — but a step that made exactly one action is now folded into its
// tool span (buildSpans), which carries that step's usage while keeping `type: 'tool'`. Gating on
// the type zeroed those rows: `SpanRow` and `trajectorySummary.countTokens` both read `rolled`
// first, so every folded step rendered 0 and the Signals total undercounted the run.
export function aggregateTokens(span: Span): TokenSum {
  const children: TokenSum = {
    input: 0,
    output: 0,
    reasoning: 0,
    cacheRead: 0,
    cacheWrite: 0,
  };
  for (const child of span.children ?? []) {
    const c = aggregateTokens(child);
    children.input += c.input;
    children.output += c.output;
    children.reasoning += c.reasoning;
    children.cacheRead += c.cacheRead;
    children.cacheWrite += c.cacheWrite;
  }
  const total: TokenSum = {
    input: children.input + (span.inputTokens ?? 0),
    output: children.output + (span.outputTokens ?? 0),
    reasoning: children.reasoning + (span.reasoningTokens ?? 0),
    cacheRead: children.cacheRead + (span.cacheReadTokens ?? 0),
    cacheWrite: children.cacheWrite + (span.cacheWriteTokens ?? 0),
  };
  span.rolled = total;
  // A subagent/phase row inherits the model of the calls nested inside it.
  if (!span.model) {
    for (const child of span.children ?? []) {
      if (child.model) {
        span.model = child.model;
        break;
      }
    }
  }
  return total;
}

/** Token fields lifted off an LLM step message, so a folded row keeps the step's usage. */
type LlmTokens = Pick<
  Span,
  | 'inputTokens'
  | 'outputTokens'
  | 'reasoningTokens'
  | 'cacheReadTokens'
  | 'cacheWriteTokens'
>;

/**
 * A span that owns a subtree — now or later — must keep its own row.
 *
 * A subagent's iterations and a phase's child-run timeline are attached AFTER buildSpans runs
 * (see `useRunSpans`), so a childless subagent here is not a leaf; it's a leaf that hasn't loaded
 * yet. Folding it into its step would leave that subtree with nothing to hang from.
 */
function ownsSubtree(span: Span): boolean {
  return (
    !!span.children?.length ||
    !!span.isSubagent ||
    !!span.agentCard ||
    !!span.childAgentRunId ||
    !!span.childWorkflowRunId
  );
}

/**
 * Collapse a step that did exactly one thing into ONE row, or null when it doesn't qualify.
 *
 * `deriveLlmStepName` already titles such a step after its only tool ("Left Click"), so the pair
 * renders as `Left Click › left_click` — the same word twice, one nested in the other, with the
 * action's reasoning and screenshot stranded on the inner row. Merging halves the depth of an
 * operator trail and, because the surviving id is the TOOL CALL's, makes the visible row the one
 * that scrubs the screenshot gallery.
 */
// Cyclomatic 10 is the eligibility rules themselves (one tool call, no subagent subtree, LLM
// error wins, reasoning/duration fallbacks). Splitting them would scatter one decision across
// several functions; the cyclomatic ceiling of 20 is untouched.
// fallow-ignore-next-line complexity
function foldSingleActionStep({
  children,
  msg,
  name,
  llmTokens,
  fallbackModel,
  llmDurMs,
  eligible,
}: {
  children: Span[];
  msg: AgentMessageResponse;
  name: string;
  llmTokens: LlmTokens;
  fallbackModel: string | null;
  llmDurMs: number | null;
  /** The step made exactly one tool call and spawned no subagent iterations. */
  eligible: boolean;
}): Span | null {
  const onlyChild = eligible && children.length === 1 ? children[0] : null;
  if (!onlyChild || onlyChild.type !== 'tool' || ownsSubtree(onlyChild)) {
    return null;
  }
  return {
    ...onlyChild,
    ...llmTokens,
    name,
    model: msg.model ?? fallbackModel,
    // The LLM's own failure wins — a tool that "completed" during a step the model errored on
    // did not, in the sense that matters to someone reading the trace.
    status: msg.finishReason === 'error' ? 'failed' : onlyChild.status,
    // The operator's per-action rationale is the specific one; the step's extended-thinking
    // output is the fallback for models that report it instead.
    reasoning: onlyChild.reasoning ?? msg.reasoning,
    text: msg.text,
    finishReason: msg.finishReason,
    durMs: onlyChild.durMs ?? llmDurMs,
  };
}

export function buildSpans(
  messages: AgentMessageResponse[],
  toolCalls: ToolCallRunResponse[],
  runStartedAt: string,
  runModel?: string | null,
  /** The steps of the workflows this run's setup/teardown phases launched (`getRunTimeline`). */
  workflowPhaseSteps: readonly WorkflowPhaseStep[] = [],
): Span[] {
  const runStart = parseDate(runStartedAt)?.getTime() ?? 0;
  const fallbackModel = runModel ?? null;

  const assistantMsgs = messages.filter((m) => m.role === 'assistant');

  // Real LLM steps have step_number set; audit iteration records do not.
  // Audit records are created during a step's tool execution (before the step
  // message is persisted), so they appear chronologically before their parent
  // step's message but logically belong to it.
  const stepMsgs = assistantMsgs
    .filter((m) => m.stepNumber != null)
    .sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0));

  const auditMsgs = assistantMsgs
    .filter((m) => m.stepNumber == null)
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  const toolsByMessage = new Map<string, ToolCallRunResponse[]>();
  for (const tc of toolCalls) {
    const key = tc.agentMessageId ?? '__orphan__';
    const arr = toolsByMessage.get(key) ?? [];
    arr.push(tc);
    toolsByMessage.set(key, arr);
  }

  // Fall back to treating all messages as real steps when there are no
  // step-numbered messages (older runs or non-stepped agents).
  const sourceMsgs = stepMsgs.length > 0 ? stepMsgs : auditMsgs;

  const stepSpans = sourceMsgs.map((msg, idx, arr): Span => {
    const msgCreatedAt = new Date(msg.createdAt).getTime();
    const startMs = Math.max(0, msgCreatedAt - runStart);

    const nextMsgStart =
      idx + 1 < arr.length ? new Date(arr[idx + 1].createdAt).getTime() : null;
    const llmDurMs = nextMsgStart ? nextMsgStart - msgCreatedAt : null;

    // Audit messages that belong to this step: those created after the
    // previous real step and at or before this real step's message timestamp.
    // (They were written during this step's tool execution, before the step
    // message was persisted.)
    const prevStepCreatedAt =
      idx > 0 ? new Date(arr[idx - 1].createdAt).getTime() : 0;

    const stepAuditMsgs =
      stepMsgs.length > 0
        ? auditMsgs.filter((am) => {
            const t = new Date(am.createdAt).getTime();
            return t > prevStepCreatedAt && t <= msgCreatedAt;
          })
        : [];

    // Audit sub-steps: a subagent's own model calls (token-bearing → nested LLM
    // spans) and/or plain tool spans. See buildAuditSubSpans.
    const auditSubSpans = buildAuditSubSpans(
      stepAuditMsgs,
      toolsByMessage,
      runStart,
      startMs,
      fallbackModel,
    );

    // Build tool spans for this step's own tool calls
    const stepToolCalls = toolsByMessage.get(msg.id) ?? [];
    let children: Span[];

    if (stepToolCalls.length === 1 && auditSubSpans.length > 0) {
      // Nest audit sub-steps inside the single tool call span so they appear
      // indented under the parent tool (e.g. the web_browser_execute subagent).
      const parentSpan = buildToolSpan(stepToolCalls[0], runStart, startMs);
      // Anchor the last sub-step to the parent's end time so the inferred
      // duration covers the full window rather than collapsing to 0.
      const parentEndMs =
        parentSpan.durMs != null
          ? parentSpan.startMs + parentSpan.durMs
          : startMs;
      const anchorMs = Math.max(parentEndMs, startMs);
      inferAuditDurations(auditSubSpans, anchorMs);
      // Durations are inferred from the FLAT order, so group only after — a synthetic step span
      // has no timestamps of its own and would break the inference.
      parentSpan.children = groupSopReplaySpans(
        auditSubSpans,
        parentSpan.toolCallId ?? null,
      );
      children = [parentSpan];
    } else {
      // Infer durations anchored to this step's startMs.
      inferAuditDurations(auditSubSpans, startMs);
      children = [
        ...groupSopReplaySpans(auditSubSpans, soleOperatorHost(stepToolCalls)),
        ...stepToolCalls.map((tc) => buildToolSpan(tc, runStart, startMs)),
      ];
    }

    const status: SpanStatus =
      msg.finishReason === 'error'
        ? 'failed'
        : msg.finishReason
          ? 'completed'
          : 'running';

    const toolCount = children.length;
    const preview =
      msg.text?.slice(0, 100) ??
      (toolCount > 0
        ? `${toolCount} tool call${toolCount > 1 ? 's' : ''}`
        : null);

    const name = deriveLlmStepName(
      stepToolCalls,
      msg.stepNumber,
      idx,
      idx === arr.length - 1,
      msg.finishReason,
    );

    const llmTokens = {
      inputTokens: msg.inputTokens,
      outputTokens: msg.outputTokens,
      reasoningTokens: msg.reasoningTokens,
      cacheReadTokens: msg.cacheReadTokens,
      cacheWriteTokens: msg.cacheWriteTokens,
    };

    // A step that did exactly one thing is ONE row, not a parent named after its only child.
    //
    // `deriveLlmStepName` already titles such a step after its tool ("Left Click"), so the pair
    // renders as `Left Click › left_click` — the same word twice, one nested inside the other,
    // with the action's reasoning and screenshot stranded on the inner row. Merging halves the
    // depth of an operator trail and, because the surviving id is the TOOL CALL's, makes the
    // visible row the one that scrubs the screenshot gallery.
    const folded = foldSingleActionStep({
      children,
      msg,
      name,
      llmTokens,
      fallbackModel,
      llmDurMs,
      eligible: stepToolCalls.length === 1 && auditSubSpans.length === 0,
    });
    if (folded) return folded;

    return {
      id: msg.id,
      type: 'llm',
      name,
      model: msg.model ?? fallbackModel,
      status,
      startMs,
      durMs: llmDurMs,
      ...llmTokens,
      preview,
      children,
      reasoning: msg.reasoning,
      text: msg.text,
      finishReason: msg.finishReason,
    };
  });

  // Orphan tool calls (no agentMessageId) that represent task-loop phases:
  // these are the loop's setup/task/teardown spans. The loop runs no LLM
  // itself, so without this it would render empty. Child agent-run timelines
  // are nested under these spans downstream (see AgentTraceTable).
  const phaseSpans = toolCalls
    .filter((tc) => tc.agentMessageId == null && phaseSpanLabel(tc) != null)
    .map((tc): Span => {
      const span = buildToolSpan(tc, runStart, 0);
      span.name = phaseSpanLabel(tc) ?? span.name;
      span.isPhase = true;
      // Surface the task's verdict on the row so the loop's per-task breakdown
      // reads at a glance (the child run's metrics roll up via aggregateTokens).
      const outcome = (
        tc.outputData as Record<string, unknown> | null | undefined
      )?.taskOutcome;
      if (typeof outcome === 'string' && outcome.length > 0) {
        span.taskOutcome = outcome;
        span.preview = `Outcome: ${outcome}`;
      }
      return span;
    });

  // A workflowAction phase runs its operator inside a separate workflow, but
  // those operator iterations are persisted against THIS loop run (the loop's
  // agentRunId is threaded into the workflow's steps). They arrive as flat
  // top-level spans; re-parent any that fall within a workflow phase's time
  // window so they nest under it instead of floating as siblings.
  //
  // A setup/teardown phase is a workflow phase by its tool type, whether or not its workflow run
  // could be linked back to it: a retried [SETUP] whose run row carries no `spawnedByStepRunId`
  // still drove the login actions inside its window.
  const workflowPhaseIds = new Set(
    toolCalls
      .filter(
        (tc) =>
          tc.childWorkflowRunId != null ||
          tc.toolType === 'workflow_setup' ||
          tc.toolType === 'workflow_teardown',
      )
      .map((tc) => tc.id),
  );
  const workflowPhaseSpans = phaseSpans.filter((s) =>
    workflowPhaseIds.has(s.id),
  );
  const absorbed = new Set<Span>();
  for (const phase of workflowPhaseSpans) {
    const phaseEnd = phase.startMs + (phase.durMs ?? Number.POSITIVE_INFINITY);
    // Claimed once: a retried phase's attempts sit back to back, and a row must not land in both.
    const children = stepSpans.filter(
      (s) =>
        !absorbed.has(s) && s.startMs >= phase.startMs && s.startMs <= phaseEnd,
    );
    for (const child of children) absorbed.add(child);
    phase.children = [...(phase.children ?? []), ...children].sort(
      (a, b) => a.startMs - b.startMs,
    );
    nestUnderWorkflowSteps(phase, workflowPhaseSteps, runStart);
  }

  // Tool calls that are still executing. Their StepRun is written the moment the
  // call is dispatched, which is before the step's assistant message exists — so
  // they have no parent to nest under yet and would otherwise be invisible until
  // the whole step settled. Surface them as top-level spans so a live trace shows
  // what the agent is doing right now; once the step settles, the row is linked
  // to its message and nests normally.
  const inFlightToolSpans: Span[] = [];
  for (const tc of toolCalls) {
    if (
      tc.agentMessageId != null ||
      tc.status !== 'running' ||
      phaseSpanLabel(tc) != null
    ) {
      continue;
    }
    const span = buildToolSpan(tc, runStart, 0);
    // Title-cased like the phase/step rows it sits beside, since this row is
    // top-level rather than nested under a turn.
    span.name = formatToolDisplayName(span.name);
    inFlightToolSpans.push(span);
  }

  // Audit rows newer than the LAST settled step belong to a step still in flight: the operator
  // persists its per-action rows as they stream (AI-396) precisely so any replica's timeline poll
  // can show them, but the enclosing step's message doesn't exist until the tool returns, so the
  // window filter above never claims them. Without this they are invisible while the operator
  // runs — and invisible FOREVER when the run dies before its step message lands. Nest them under
  // the in-flight subagent span they belong to, or surface them top-level when there isn't
  // exactly one to hang them from.
  const trailingRootSpans: Span[] = [];
  if (stepMsgs.length > 0) {
    const lastStepCreatedAt = new Date(
      stepMsgs[stepMsgs.length - 1].createdAt,
    ).getTime();
    const trailingAuditMsgs = auditMsgs.filter(
      (am) => new Date(am.createdAt).getTime() > lastStepCreatedAt,
    );
    const trailingSubSpans = buildAuditSubSpans(
      trailingAuditMsgs,
      toolsByMessage,
      runStart,
      Math.max(0, lastStepCreatedAt - runStart),
      fallbackModel,
    );
    if (trailingSubSpans.length > 0) {
      const lastStartMs = Math.max(...trailingSubSpans.map((s) => s.startMs));
      inferAuditDurations(trailingSubSpans, lastStartMs);
      const hosts = inFlightToolSpans.filter((s) => s.isSubagent);
      const grouped = groupSopReplaySpans(
        trailingSubSpans,
        hosts.length === 1 ? (hosts[0].toolCallId ?? null) : null,
      );
      if (hosts.length === 1) {
        hosts[0].children = [...(hosts[0].children ?? []), ...grouped];
      } else {
        trailingRootSpans.push(...grouped);
      }
    }
  }

  const topStepSpans = stepSpans.filter((s) => !absorbed.has(s));
  const roots = [
    ...topStepSpans,
    ...phaseSpans,
    ...inFlightToolSpans,
    ...trailingRootSpans,
  ];
  const ownedRoots = attachOwnedProcedures(roots);
  ownedRoots.forEach(coverChildren);
  // Surface aggregate token usage on subagent/tool spans from their nested LLM
  // calls (e.g. web_browser_execute sums the operator's per-action model calls).
  ownedRoots.forEach(aggregateTokens);
  return sortSpanTreeByStart(ownedRoots);
}

export function flattenSpans(
  spans: Span[],
  depth: number,
  expanded: Set<string>,
  out: FlatRow[],
): FlatRow[] {
  for (const s of spans) {
    out.push({
      ...s,
      depth,
      hasChildren: !!(s.children && s.children.length > 0),
    });
    if (s.children && expanded.has(s.id)) {
      flattenSpans(s.children, depth + 1, expanded, out);
    }
  }
  return out;
}

function passesFilters(s: Span, filters: TraceFilters, query: string): boolean {
  if (filters.types.size > 0 && !filters.types.has(s.type)) return false;
  if (filters.statuses.size > 0 && !filters.statuses.has(s.status))
    return false;
  if (filters.minTokens > 0) {
    const total =
      (s.rolled?.input ?? s.inputTokens ?? 0) +
      (s.rolled?.output ?? s.outputTokens ?? 0);
    if (total < filters.minTokens) return false;
  }
  if (query) {
    const q = query.toLowerCase();
    if (
      !s.name.toLowerCase().includes(q) &&
      !s.preview?.toLowerCase().includes(q)
    )
      return false;
  }
  return true;
}

export function filterTree(
  spans: Span[],
  filters: TraceFilters,
  query: string,
): Span[] {
  const result: Span[] = [];
  for (const s of spans) {
    const childResults = s.children
      ? filterTree(s.children, filters, query)
      : [];
    if (passesFilters(s, filters, query) || childResults.length > 0) {
      result.push({ ...s, children: childResults });
    }
  }
  return result;
}

export function countSpansByType(spans: Span[]): Record<SpanType, number> {
  const counts: Record<SpanType, number> = { llm: 0, tool: 0 };
  function walk(ss: Span[]) {
    for (const s of ss) {
      counts[s.type]++;
      if (s.children) walk(s.children);
    }
  }
  walk(spans);
  return counts;
}

export function countTotalSpans(spans: Span[]): number {
  let n = 0;
  function walk(ss: Span[]) {
    n += ss.length;
    for (const s of ss) if (s.children) walk(s.children);
  }
  walk(spans);
  return n;
}
